import type { ScheduledSession } from '@fs/zion-rootstech'
import scheduleConfig from '../../../config/schedule'

const { storageKey: STORAGE_KEY, lifetimeMs: INTENT_LIFETIME_MS } = scheduleConfig.signInIntent

interface ScheduleSignInIntent {
  token: string
  expiresAt: number
  session: ScheduledSession
}

/** Stores a selection in this tab and returns its authentication return parameters. */
export function createScheduleSignInQuery(session: ScheduledSession): Record<string, string> | undefined {
  try {
    const bytes = new Uint8Array(32)
    window.crypto.getRandomValues(bytes)
    const token = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
    const intent: ScheduleSignInIntent = { token, session, expiresAt: Date.now() + INTENT_LIFETIME_MS }
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(intent))
    return {
      scheduleIntent: token,
      addToSchedule: session.contentId,
      scheduleCalendarDate: String(session.calendarDate),
      scheduleDate: session.date,
      scheduleTime: session.time,
    }
  } catch {
    return undefined
  }
}

/** Verifies the return parameters against the selection made in this tab. */
export function hasScheduleSignInIntent(token: unknown, session: ScheduledSession): boolean {
  if (typeof token !== 'string') return false
  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY)
    if (!stored) return false
    const intent: ScheduleSignInIntent = JSON.parse(stored)
    return (
      intent?.token === token &&
      Number.isFinite(intent.expiresAt) &&
      intent.expiresAt > Date.now() &&
      intent.session?.contentId === session.contentId &&
      intent.session.calendarDate === session.calendarDate &&
      intent.session.date === session.date &&
      intent.session.time === session.time
    )
  } catch {
    return false
  }
}

/** Consumes a matching selection before the authenticated mutation. */
export function consumeScheduleSignInIntent(token: unknown, session: ScheduledSession): boolean {
  if (!hasScheduleSignInIntent(token, session)) return false
  try {
    window.sessionStorage.removeItem(STORAGE_KEY)
    return true
  } catch {
    return false
  }
}
